import React from 'react';
import { cn } from '@/lib/utils';
import { motion, HTMLMotionProps } from 'motion/react';

export interface ButtonProps extends HTMLMotionProps<'button'> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'link';
  size?: 'sm' | 'md' | 'lg';
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', ...props }, ref) => {
    const baseStyles = 'inline-flex items-center justify-center whitespace-nowrap font-bold transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 uppercase tracking-widest rounded-none';
    
    const variants = {
      primary: 'bg-ink text-white hover:bg-rule-3 hover:text-white',
      secondary: 'bg-ink text-ground hover:bg-rule-3 hover:text-white',
      outline: 'border border-ink text-ink hover:bg-rule-3 hover:border-rule-3 hover:text-white',
      ghost: 'hover:bg-ink/5 text-ink hover:text-rule-3',
      link: 'text-ink underline-offset-4 hover:text-rule-3 hover:underline',
    };

    const sizes = {
      sm: 'h-10 px-6 text-[12px]',
      md: 'h-12 px-8 text-xs',
      lg: 'h-14 px-8 text-xs',
    };

    return (
      <motion.button
        ref={ref}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 400, damping: 17 }}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';
